import React, { useState, useEffect } from 'react';
import { 
  ArrowRight, 
  MessageCircle, 
  CheckCircle2, 
  Star, 
  Sparkles,
  Palette,
  Package,
  Clock,
  Shield,
  Play,
  ChevronDown,
  Heart,
  Gift,
  Award
} from 'lucide-react';
import { getSettings, getCevadorConfig, getLandingConfig } from '../services/api';
import { initMetaPixel, trackViewContent, trackLead, trackContact } from '../utils/metaPixel';

const ICON_MAP = {
  Sparkles: Sparkles,
  Shield: Shield,
  Palette: Palette,
  Clock: Clock,
  Heart: Heart,
  Gift: Gift,
  Award: Award,
};

const CevadorLanding = () => {
  const [settings, setSettings] = useState(null);
  const [priceConfig, setPriceConfig] = useState(null);
  const [landingConfig, setLandingConfig] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsData, configData, landingData] = await Promise.all([
          getSettings(),
          getCevadorConfig(),
          getLandingConfig()
        ]);
        setSettings(settingsData);
        setPriceConfig(configData);
        setLandingConfig(landingData);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    
    // Initialize Meta Pixel
    initMetaPixel();
    
    // Track ViewContent when page loads
    trackViewContent('Landing Cevadores', 'Cevadores Personalizados', 0);
  }, []);

  const whatsappNumber = settings?.whatsapp || '5555984551621';
  const whatsappLink = `https://wa.me/${whatsappNumber.replace(/\D/g, '')}?text=Olá! Gostaria de saber mais sobre os cevadores personalizados.`;

  // Default gallery images (fallback)
  const defaultGalleryImages = [
    'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1572635196237-14b3f281503f?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=600&h=600&fit=crop',
    'https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=600&h=600&fit=crop',
  ];

  // Use landing config data or defaults
  const config = landingConfig || {};
  
  const galleryImages = config.gallery_images?.length > 0 
    ? config.gallery_images.map(img => img.url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${img.url}` : img.url)
    : defaultGalleryImages;

  const features = config.features?.length > 0 ? config.features : [
    { icon: 'Sparkles', title: 'Impressão HD', description: 'Qualidade fotográfica com cores vibrantes e duráveis' },
    { icon: 'Shield', title: 'Material Premium', description: 'Aço inox 304 de alta qualidade, resistente e durável' },
    { icon: 'Palette', title: 'Personalização Total', description: 'Sua arte, logo ou foto impressa com perfeição' },
    { icon: 'Clock', title: 'Entrega Rápida', description: 'Produção ágil para atender seu prazo' },
  ];

  const testimonials = config.testimonials?.length > 0 ? config.testimonials : [
    { name: 'Maria Silva', text: 'Qualidade incrível! Os cevadores ficaram perfeitos para o evento da empresa.', rating: 5 },
    { name: 'João Santos', text: 'Atendimento excelente e entrega antes do prazo. Super recomendo!', rating: 5 },
    { name: 'Ana Costa', text: 'Já é a terceira vez que compro. Sempre com a mesma qualidade impecável.', rating: 5 },
  ];

  const prices = priceConfig?.prices || [
    { min_quantity: 20, max_quantity: 30, price: 3.50 },
    { min_quantity: 31, max_quantity: 49, price: 3.20 },
    { min_quantity: 50, max_quantity: null, price: 2.90 },
  ];

  const qualityPoints = config.quality_points || [
    'Impressão sublimática de alta definição',
    'Cores resistentes a lavagens e uso diário',
    'Acabamento profissional e durável',
    'Fidelidade às cores do seu design'
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-[#195C75] flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Header with Logo */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <a href="/" className="flex items-center">
            {settings?.logo_url ? (
              <img 
                src={settings.logo_url} 
                alt="Gelatti" 
                className="h-10 lg:h-12 object-contain"
              />
            ) : (
              <span className="text-xl lg:text-2xl font-bold text-[#195C75]">Gelatti</span>
            )}
          </a>
          <a
            href="/cevador"
            onClick={() => trackLead('Monte seu Pedido - Header', 'header_cta')}
            className="inline-flex items-center gap-2 bg-[#195C75] text-white font-medium px-4 py-2 rounded-full hover:bg-[#195C75]/90 transition-colors text-sm"
          >
            <Sparkles className="w-4 h-4" />
            Monte seu Pedido
          </a>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#195C75] via-[#1a6b87] to-[#0d4a5f] text-white overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-white rounded-full translate-x-1/3 translate-y-1/3" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
                <Sparkles className="w-4 h-4" />
                <span>{config.hero_badge || 'Qualidade Premium'}</span>
              </div>
              
              <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                {config.hero_title || 'Cevadores'}
                <span className="block text-white/90">{config.hero_subtitle || 'Personalizados'}</span>
              </h1>
              
              <p className="text-lg lg:text-xl text-white/80 mb-8 max-w-lg mx-auto lg:mx-0">
                {config.hero_description || 'Transforme seu evento, brinde corporativo ou presente especial com cevadores exclusivos de alta qualidade.'}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
                <a
                  href="/cevador"
                  onClick={() => trackLead('Monte seu Pedido - Hero', 'hero_cta_primary')}
                  className="inline-flex items-center justify-center gap-2 bg-white text-[#195C75] font-semibold px-8 py-4 rounded-full hover:bg-white/90 transition-all hover:scale-105 shadow-lg"
                >
                  {config.hero_cta_primary || 'Monte seu Pedido'}
                  <ArrowRight className="w-5 h-5" />
                </a>
                <a
                  href={whatsappLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackContact('whatsapp_hero')}
                  className="inline-flex items-center justify-center gap-2 bg-green-500 text-white font-semibold px-8 py-4 rounded-full hover:bg-green-600 transition-all hover:scale-105 shadow-lg"
                >
                  <MessageCircle className="w-5 h-5" />
                  {config.hero_cta_secondary || 'Fale Conosco'}
                </a>
              </div>

              {/* Quick Stats */}
              <div className="flex items-center justify-center lg:justify-start gap-8 mt-10 pt-8 border-t border-white/20">
                <div className="text-center">
                  <div className="text-3xl font-bold">{config.stat_clients || '500+'}</div>
                  <div className="text-sm text-white/70">{config.stat_clients_label || 'Clientes Satisfeitos'}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{config.stat_products || '10k+'}</div>
                  <div className="text-sm text-white/70">{config.stat_products_label || 'Cevadores Produzidos'}</div>
                </div>
                <div className="text-center">
                  <div className="text-3xl font-bold">{config.stat_rating || '5'}</div>
                  <div className="flex items-center gap-1 text-sm text-white/70">
                    <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    {config.stat_rating_label || 'Avaliação'}
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Image Showcase */}
            <div className="relative">
              <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-4 lg:p-6">
                <div className="aspect-square rounded-2xl overflow-hidden bg-white/5">
                  <img 
                    src={galleryImages[activeImage]}
                    alt="Cevador personalizado"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Thumbnails */}
                <div className="flex gap-2 mt-4 justify-center">
                  {galleryImages.slice(0, 4).map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setActiveImage(idx)}
                      className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        activeImage === idx ? 'border-white scale-110' : 'border-transparent opacity-60 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              </div>

              {/* Floating Badge - positioned below thumbnails on mobile, overlapping on desktop */}
              <div className="flex justify-center lg:block lg:absolute mt-4 lg:mt-0 lg:-bottom-6 lg:-left-6">
                <div className="bg-white text-[#195C75] px-6 py-3 rounded-2xl shadow-xl">
                  <div className="text-sm font-medium">A partir de</div>
                  <div className="text-2xl font-bold">R$ {prices[prices.length - 1]?.price?.toFixed(2).replace('.', ',') || '2,90'}</div>
                  <div className="text-xs text-slate-500">por unidade</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce">
          <ChevronDown className="w-8 h-8 text-white/50" />
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              {config.features_title || 'Por que escolher nossos Cevadores?'}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {config.features_subtitle || 'Qualidade superior em cada detalhe, do material à impressão'}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, idx) => {
              const IconComponent = ICON_MAP[feature.icon] || Sparkles;
              return (
                <div 
                  key={idx}
                  className="bg-white rounded-2xl p-6 shadow-sm hover:shadow-lg transition-shadow border border-slate-100"
                >
                  <div className="w-12 h-12 bg-[#195C75]/10 rounded-xl flex items-center justify-center mb-4">
                    <IconComponent className="w-6 h-6 text-[#195C75]" />
                  </div>
                  <h3 className="font-semibold text-slate-800 mb-2">{feature.title}</h3>
                  <p className="text-sm text-slate-600">{feature.description || feature.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-16 lg:py-24">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              {config.pricing_title || 'Tabela de Preços'}
            </h2>
            <p className="text-slate-600 max-w-2xl mx-auto">
              {config.pricing_subtitle || 'Quanto maior a quantidade, menor o preço unitário'}
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6 mb-10">
            {prices.map((price, idx) => (
              <div 
                key={idx}
                className={`relative bg-white rounded-2xl p-8 shadow-lg border-2 transition-all hover:scale-105 ${
                  idx === prices.length - 1 ? 'border-[#195C75]' : 'border-slate-200'
                }`}
              >
                {idx === prices.length - 1 && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#195C75] text-white text-xs font-medium px-3 py-1 rounded-full">
                    Melhor Preço
                  </div>
                )}
                <div className="text-center">
                  <div className="text-sm text-slate-500 mb-2">
                    {price.max_quantity 
                      ? `${price.min_quantity} a ${price.max_quantity} unidades`
                      : `${price.min_quantity}+ unidades`
                    }
                  </div>
                  <div className="text-4xl font-bold text-[#195C75] mb-1">
                    R$ {price.price.toFixed(2).replace('.', ',')}
                  </div>
                  <div className="text-sm text-slate-500">por unidade</div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-700 px-4 py-2 rounded-full text-sm mb-6">
              <Package className="w-4 h-4" />
              Pedido mínimo: {priceConfig?.min_quantity || 20} unidades
            </div>
            <div className="block">
              <a
                href="/cevador"
                onClick={() => trackLead('Fazer Pedido Agora - Preços', 'pricing_cta')}
                className="inline-flex items-center justify-center gap-2 bg-[#195C75] text-white font-semibold px-8 py-4 rounded-full hover:bg-[#195C75]/90 transition-all hover:scale-105 shadow-lg"
              >
                Fazer Pedido Agora
                <ArrowRight className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Quality Section */}
      <section className="py-16 lg:py-24 bg-slate-900 text-white">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                {config.quality_title || 'Qualidade de Impressão'}
                <span className="text-[#4db8d8]"> {config.quality_highlight || 'Premium'}</span>
              </h2>
              <p className="text-slate-300 mb-8">
                {config.quality_description || 'Utilizamos tecnologia de impressão de última geração para garantir cores vibrantes, detalhes nítidos e durabilidade excepcional em cada cevador.'}
              </p>
              
              <ul className="space-y-4">
                {qualityPoints.map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-400 flex-shrink-0" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <a
                href={whatsappLink}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackContact('whatsapp_quality')}
                className="inline-flex items-center gap-2 bg-green-500 text-white font-semibold px-6 py-3 rounded-full hover:bg-green-600 transition-all mt-8"
              >
                <MessageCircle className="w-5 h-5" />
                Tire suas Dúvidas
              </a>
            </div>

            <div className="relative">
              <div className="aspect-video bg-slate-800 rounded-2xl overflow-hidden flex items-center justify-center">
                {config.video_file_url ? (
                  <video
                    src={config.video_file_url.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${config.video_file_url}` : config.video_file_url}
                    controls
                    className="w-full h-full object-contain"
                    poster=""
                  >
                    Seu navegador não suporta vídeos HTML5.
                  </video>
                ) : config.video_url ? (
                  <iframe
                    src={config.video_url.replace('watch?v=', 'embed/')}
                    title="Vídeo de produção"
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <div className="text-center">
                    <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center mx-auto mb-4 cursor-pointer hover:bg-white/20 transition-colors">
                      <Play className="w-8 h-8 text-white ml-1" />
                    </div>
                    <p className="text-slate-400">Veja o processo de produção</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 lg:py-24 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-slate-800 mb-4">
              {config.testimonials_title || 'O que nossos clientes dizem'}
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, idx) => (
              <div 
                key={idx}
                className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating || 5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  ))}
                </div>
                <p className="text-slate-600 mb-4">"{testimonial.text}"</p>
                <div className="font-medium text-slate-800">{testimonial.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 lg:py-24 bg-gradient-to-br from-[#195C75] to-[#0d4a5f] text-white">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            {config.cta_title || 'Pronto para criar seus Cevadores?'}
          </h2>
          <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto">
            {config.cta_description || 'Monte seu pedido online ou fale conosco pelo WhatsApp para um atendimento personalizado.'}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/cevador"
              onClick={() => trackLead('Monte seu Pedido - CTA Final', 'cta_final')}
              className="inline-flex items-center justify-center gap-2 bg-white text-[#195C75] font-semibold px-8 py-4 rounded-full hover:bg-white/90 transition-all hover:scale-105 shadow-lg"
            >
              <Package className="w-5 h-5" />
              Monte seu Pedido
            </a>
            <a
              href={whatsappLink}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackContact('whatsapp_cta_final')}
              className="inline-flex items-center justify-center gap-2 bg-green-500 text-white font-semibold px-8 py-4 rounded-full hover:bg-green-600 transition-all hover:scale-105 shadow-lg"
            >
              <MessageCircle className="w-5 h-5" />
              WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <div className="text-2xl font-bold mb-2">Gelatti</div>
          <p className="text-slate-400 text-sm">
            Cevadores personalizados de alta qualidade
          </p>
          <div className="mt-4 pt-4 border-t border-slate-800 text-slate-500 text-sm">
            © {new Date().getFullYear()} Gelatti. Todos os direitos reservados.
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        onClick={() => trackContact('whatsapp_floating')}
        className="fixed bottom-6 right-6 w-14 h-14 bg-green-500 text-white rounded-full flex items-center justify-center shadow-lg hover:bg-green-600 hover:scale-110 transition-all z-50"
        title="Fale conosco no WhatsApp"
      >
        <MessageCircle className="w-7 h-7" />
      </a>
    </div>
  );
};

export default CevadorLanding;
